import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";

type Props = {
  ariaLabel: string;
  children: ReactNode;
  triggerClassName: string;
  popoverClassName: string;
};

export default function InfoPopover({
  ariaLabel,
  children,
  triggerClassName,
  popoverClassName,
}: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="contents">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-controls={popoverId}
        aria-describedby={open ? popoverId : undefined}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={triggerClassName}
      >
        i
      </button>
      {open ? (
        <div id={popoverId} role="tooltip" className={popoverClassName}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
